import React, { useState } from 'react';
import { Box, Text, Flex, Button, Input } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';

const MotionDiv = motion.div;
const MotionBox = motion(Box);

function Hero() {
  const shouldReduce = useReducedMotion();
  const [email, setEmail]     = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSignup = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitted(true);
  };

  return (
    <Box as="section" id="hero"
      aria-label="Hero — O-ID Modular Humanoid Robotics"
      position="relative" height="100vh"
      bg="oid.bg"
      display="flex" flexDirection="column"
      justifyContent="center"
      pt="var(--nav-h)" overflow="hidden">

      {/* ── Centred video — portrait, full height ── */}
      <Box position="absolute" top={0} bottom={0} left="50%"
        width="auto" zIndex={0} aria-hidden="true"
        sx={{ transform: 'translateX(-50%)' }}>
        <video
          autoPlay muted playsInline
          style={{ height: '100%', width: 'auto', display: 'block' }}>
          <source src="/o-id-arms-attach.webm" type="video/webm" />
          <source src="/o-id-arms-attach.mp4"  type="video/mp4"  />
        </video>
      </Box>


      {/* ── Left content ── */}
      <Box position="relative" zIndex={2} width="100%">
      <Box maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }}>
      <Box maxWidth={{ base: '100%', lg: '50%' }} py={{ base: 16, lg: 0 }}>

        <MotionDiv
          initial={shouldReduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.05, ease: [0.25, 0.1, 0.25, 1] }}>
          <Text fontSize="11px" fontWeight={600} letterSpacing="0.34em"
            textTransform="uppercase" color="oid.accent" mb={8}>
            Modular Humanoid Robotics, Series I
          </Text>
        </MotionDiv>

        {/* Logo image */}
        <Box overflow="hidden" mb={10}>
          <MotionDiv
            initial={shouldReduce ? false : { y: '108%' }}
            animate={{ y: 0 }}
            transition={{ duration: 1.05, delay: 0.18, ease: [0.25, 0.1, 0.25, 1] }}>
            <Box as="img"
              src="/o-id-logo-with-text.png"
              alt="O-ID"
              sx={{
                width: 'min(420px, 72vw)',
                height: 'auto',
                display: 'block',
                mixBlendMode: 'multiply',
              }}
            />
          </MotionDiv>
        </Box>

        {/* Tagline */}
        <MotionDiv
          initial={shouldReduce ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.52, ease: [0.25, 0.1, 0.25, 1] }}>
          <Text fontSize={{ base: '1rem', md: '1.0625rem' }} fontWeight={400}
            color="oid.muted" maxWidth="420px" lineHeight={1.618} mb={10}>
            <Text as="strong" fontWeight={700} color="oid.text">Field serviceable.</Text>{' '}
            Infinitely adaptable. A humanoid platform engineered for the real world,{' '}
            <Text as="strong" fontWeight={700} color="oid.text">maintained in minutes,</Text>{' '}
            configured for any mission.

          </Text>
        </MotionDiv>

        {/* CTA buttons */}
        <MotionDiv
          initial={shouldReduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.72, ease: [0.25, 0.1, 0.25, 1] }}>
          <Flex gap={3} mb={10} flexWrap="wrap">
            <Button
              as="a" href="#modular"
              size="md" px={7} py={5}
              fontSize="11px" letterSpacing="0.14em" textTransform="uppercase">
              Explore Platform
            </Button>
            <Button
              as="a" href="#contact-form"
              variant="outline"
              size="md" px={7} py={5}
              fontSize="11px" letterSpacing="0.14em" textTransform="uppercase">
              Request Access
            </Button>
          </Flex>
        </MotionDiv>

        {/* Email signup */}
        <MotionDiv
          initial={shouldReduce ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.88, ease: [0.25, 0.1, 0.25, 1] }}>
          <Box maxWidth="380px">
            <Text fontSize="10px" fontWeight={600} letterSpacing="0.24em"
              textTransform="uppercase" color="rgba(22,33,58,0.4)" mb={3}>
              Stay updated
            </Text>
            {submitted ? (
              <Flex align="center" gap={3} height="44px">
                <Box width="6px" height="6px" borderRadius="full" bg="oid.accent" />
                <Text fontSize="sm" fontWeight={500} color="oid.text">
                  You're on the list. We'll be in touch.
                </Text>
              </Flex>
            ) : (
              <Flex as="form" onSubmit={handleSignup} gap={2}>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  flex={1}
                  bg="white"
                  border="1px solid"
                  borderColor="rgba(22,33,58,0.18)"
                  borderRadius="2px"
                  fontSize="sm"
                  color="oid.text"
                  _placeholder={{ color: 'rgba(22,33,58,0.35)' }}
                  _focus={{ borderColor: 'oid.accent', boxShadow: 'none' }}
                  height="44px"
                />
                <Button
                  type="submit"
                  height="44px"
                  px={5}
                  fontSize="10px"
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                  flexShrink={0}>
                  Sign Up
                </Button>
              </Flex>
            )}
          </Box>
        </MotionDiv>
      </Box>
      </Box>
      </Box>

      {/* Bottom-left scroll indicator */}
      <MotionDiv
        initial={shouldReduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.1 }}
        style={{ position: 'absolute', bottom: '34px', left: 'clamp(24px,3vw,48px)', zIndex: 2 }}>
        <Flex align="center" gap={3}>
          <Box position="relative" width="48px" height="1px"
            bg="rgba(22,33,58,0.15)" overflow="hidden" aria-hidden="true">
            <Box position="absolute" top={0} left={0} width="100%" height="100%"
              bg="oid.accent"
              sx={{ animation: shouldReduce ? 'none' : 'lineSwipe 2.4s ease-in-out infinite' }} />
          </Box>
          <Text fontSize="10px" fontWeight={600} letterSpacing="0.3em"
            textTransform="uppercase" color="rgba(22,33,58,0.35)"
            sx={{ animation: shouldReduce ? 'none' : 'scrollPulse 2.4s ease-in-out infinite' }}>
            Scroll
          </Text>
        </Flex>
      </MotionDiv>

      <MotionDiv
        initial={shouldReduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.1 }}
        style={{ position: 'absolute', bottom: '34px', right: 'clamp(24px,3vw,48px)', zIndex: 2 }}>
        <Text fontSize="10px" fontWeight={600} letterSpacing="0.3em"
          textTransform="uppercase" color="rgba(22,33,58,0.25)" aria-hidden="true">
          Series I Field Edition
        </Text>
      </MotionDiv>
    </Box>
  );
}
export default Hero;
